import React, { useCallback } from 'react';
import { isEmpty } from 'lodash';

import { Box, Tooltip, Typography } from '@mui/material';
import EventOutlinedIcon from '@mui/icons-material/EventOutlined';
import EventRepeatOutlinedIcon from '@mui/icons-material/EventRepeatOutlined';
import ThumbDownOutlinedIcon from '@mui/icons-material/ThumbDownOutlined';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import ThumbUpOutlinedIcon from '@mui/icons-material/ThumbUpOutlined';
import QueryBuilderRoundedIcon from '@mui/icons-material/QueryBuilderRounded';
import ChatOutlinedIcon from '@mui/icons-material/ChatOutlined';

import {
  CandidateDetailsAction,
  CandidateListingStatusLabel,
  CandidateStatus,
  CandidateStatusMeaning,
  hasPermission,
} from '@constants';
import { useAppSelector } from '@redux/hooks';
import { selectPermissions } from '@redux/accounts/accounts.selectors';
import { IPositionCandidate } from '@types';
import {
  bookedSlotAt,
  formatDate,
  isInterviewOverdue,
  normalizeCandidateStatus,
} from '@utils';
import { cardButtonSx } from 'theme/buttons';
import {
  ACTION_SX,
  CARD_SECONDARY_ACTION_SX,
  ClosedStatus,
  ListActionButton,
  PendingStatus,
  SECONDARY_ACTION_SX,
  STATUS_LABEL_PROPS,
} from './ListActionParts';

const SETTLED_STATUSES = [
  CandidateStatus.CallPassed,
  CandidateStatus.HiringApproved,
  CandidateStatus.Hired,
];

const statusHint = (
  candidate: IPositionCandidate,
  status: CandidateStatus,
): string => {
  const updated = formatDate(candidate.updatedAt);
  const changed = updated ? `Updated ${updated}` : '';

  return [CandidateStatusMeaning[status], changed].filter(Boolean).join(' · ');
};

const FeedbackTally: React.FC<{ count: number }> = ({ count }) =>
  count > 0 ? (
    <Tooltip
      title={`${count} interview ${count === 1 ? 'write-up' : 'write-ups'} on file`}
      placement="top"
    >
      <Box
        display="flex"
        alignItems="center"
        gap={0.25}
        color="text.secondary"
        flexShrink={0}
      >
        <ChatOutlinedIcon sx={{ fontSize: '0.875rem' }} />
        <Typography variant="body2" color="text.secondary">
          {count}
        </Typography>
      </Box>
    </Tooltip>
  ) : null;

export const CandidateListAction: React.FC<{
  candidate: IPositionCandidate;
  onAction: (candidateId: string, action: CandidateDetailsAction) => void;
  inCard?: boolean;
}> = ({ candidate, onAction, inCard }) => {
  const candidateStatus = normalizeCandidateStatus(candidate.status);
  const canUpdateCandidate = hasPermission(
    useAppSelector(selectPermissions),
    'H:W',
  );

  const openWithAction = useCallback(
    (action: CandidateDetailsAction) => (event: React.MouseEvent) => {
      event.stopPropagation();
      onAction(candidate.id, action);
    },
    [candidate.id, onAction],
  );

  const statusLabel = candidateStatus
    ? CandidateListingStatusLabel[candidateStatus]
    : candidate.status;

  const tally = <FeedbackTally count={candidate.interviewFeedbackCount ?? 0} />;

  const actionSx = inCard ? cardButtonSx : ACTION_SX;
  const secondaryActionSx = inCard
    ? CARD_SECONDARY_ACTION_SX
    : SECONDARY_ACTION_SX;
  const skipIcon = inCard ? undefined : <ThumbDownOutlinedIcon />;
  const actionGap = inCard ? 0.5 : 0.75;
  const readOnly = !canUpdateCandidate;

  if (!candidateStatus)
    return (
      <ClosedStatus
        label={statusLabel}
        hint={statusLabel}
        reason={candidate.closingReason}
      />
    );

  switch (candidateStatus) {
    case CandidateStatus.Submitted:
      return (
        <Box display="flex" alignItems="center" gap={actionGap}>
          <ListActionButton
            hint="Books a slot. AgileEngine confirms it with the candidate"
            readOnly={readOnly}
            variant="contained"
            startIcon={<EventOutlinedIcon />}
            sx={actionSx}
            onClick={openWithAction(CandidateDetailsAction.SCHEDULE_INTERVIEW)}
          >
            Schedule interview
          </ListActionButton>
          <ListActionButton
            hint="Skip candidate"
            readOnly={readOnly}
            variant="outlined"
            startIcon={skipIcon}
            sx={secondaryActionSx}
            onClick={openWithAction(CandidateDetailsAction.SKIP_CANDIDATE)}
          >
            Skip
          </ListActionButton>
          {tally}
        </Box>
      );

    case CandidateStatus.CallScheduled: {
      const booked = isEmpty(candidate.scheduledSlot)
        ? null
        : bookedSlotAt(candidate.scheduledSlot, candidate.clientTimezone);

      if (
        isInterviewOverdue(
          candidate.status,
          candidate.scheduledSlot,
          candidate.clientTimezone,
        )
      )
        return (
          <Box display="flex" alignItems="center" gap={actionGap}>
            <ListActionButton
              hint={
                booked
                  ? `The slot on ${booked.format('MMM DD, hh:mm a')} has passed. AgileEngine confirms the new time`
                  : 'Moves the booked slot. AgileEngine confirms the new time'
              }
              readOnly={readOnly}
              variant="contained"
              startIcon={<EventRepeatOutlinedIcon />}
              sx={actionSx}
              onClick={openWithAction(
                CandidateDetailsAction.RESCHEDULE_INTERVIEW,
              )}
            >
              Reschedule interview
            </ListActionButton>
            {tally}
          </Box>
        );

      return (
        <Tooltip
          title={
            booked
              ? `Interview booked for ${booked.format('ddd, MMM DD, hh:mm a')} · ${CandidateStatusMeaning[candidateStatus]}`
              : statusHint(candidate, candidateStatus)
          }
          placement="top"
        >
          <Box
            display="flex"
            alignItems="center"
            gap={0.75}
            color="text.secondary"
            minWidth={0}
          >
            <QueryBuilderRoundedIcon sx={{ fontSize: '1rem', flexShrink: 0 }} />
            <Typography {...STATUS_LABEL_PROPS} color="text.secondary" noWrap>
              {booked ? booked.format('MMM DD, hh:mm a') : statusLabel}
            </Typography>
          </Box>
        </Tooltip>
      );
    }

    case CandidateStatus.InterviewDone:
      return (
        <Box display="flex" alignItems="center" gap={actionGap}>
          <ListActionButton
            hint="Proceed to hiring"
            readOnly={readOnly}
            variant={inCard ? 'contained' : 'outlined'}
            startIcon={inCard ? <ThumbUpIcon /> : <ThumbUpOutlinedIcon />}
            sx={inCard ? cardButtonSx : SECONDARY_ACTION_SX}
            onClick={openWithAction(
              CandidateDetailsAction.CLIENT_INTERVIEW_PASSED,
            )}
          >
            Hire
          </ListActionButton>
          <ListActionButton
            hint="Skip candidate"
            readOnly={readOnly}
            variant="outlined"
            startIcon={skipIcon}
            sx={secondaryActionSx}
            onClick={openWithAction(
              CandidateDetailsAction.CLIENT_INTERVIEW_FAILED,
            )}
          >
            Skip
          </ListActionButton>
          {tally}
        </Box>
      );

    case CandidateStatus.CallCancelled:
      return (
        <Box display="flex" alignItems="center" gap={actionGap}>
          <ListActionButton
            hint="Rebooking puts the candidate back in the pipeline. AgileEngine confirms the new time"
            readOnly={readOnly}
            variant="contained"
            startIcon={<EventRepeatOutlinedIcon />}
            sx={actionSx}
            onClick={openWithAction(
              CandidateDetailsAction.RESCHEDULE_INTERVIEW,
            )}
          >
            Reschedule interview
          </ListActionButton>
          {tally}
        </Box>
      );

    case CandidateStatus.CallRequested:
      return (
        <PendingStatus
          label={statusLabel}
          hint={statusHint(candidate, candidateStatus)}
        />
      );

    default:
      return (
        <Box display="flex" alignItems="center" gap={0.75} minWidth={0}>
          <ClosedStatus
            label={statusLabel}
            hint={statusHint(candidate, candidateStatus)}
            reason={candidate.closingReason}
            settled={SETTLED_STATUSES.includes(candidateStatus)}
          />
          {tally}
        </Box>
      );
  }
};
